import React, { useId } from 'react';
import { cx } from './cx';
import { TIME_SLOTS, toSlot } from '@/lib/publishSlots';

/** Form fields (theme board): label above, 44px controls, line ring that turns ink on focus. */
const control = 'min-h-[44px] w-full rounded-btn border-0 bg-card px-3.5 text-[15px] text-ink shadow-ring-line placeholder:text-ink-3 focus:outline-none focus:shadow-ring-ink disabled:opacity-60';

const Wrap: React.FC<{ id: string; label: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode; className?: string; children: React.ReactNode; tag?: React.ReactNode }> = ({ id, label, hint, error, className, children, tag }) => (
  <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
    <label htmlFor={id} className="flex items-center gap-2 text-small font-semibold text-ink-2">{label}{tag}</label>
    {children}
    {error ? <span className="text-xs font-semibold text-bad">{error}</span> : hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
  </div>
);

export const TextField: React.FC<Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> & { label: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode; wrapClassName?: string; tag?: React.ReactNode }> = ({
  label, hint, error, wrapClassName, className, tag, ...rest
}) => {
  const id = useId();
  return <Wrap id={id} label={label} hint={hint} error={error} className={wrapClassName} tag={tag}><input id={id} className={cx(control, className)} {...rest} /></Wrap>;
};

export const TextArea: React.FC<Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & { label: React.ReactNode; hint?: React.ReactNode; wrapClassName?: string }> = ({
  label, hint, wrapClassName, className, rows = 3, ...rest
}) => {
  const id = useId();
  return <Wrap id={id} label={label} hint={hint} className={wrapClassName}><textarea id={id} rows={rows} className={cx(control, 'resize-y py-3 leading-normal', className)} {...rest} /></Wrap>;
};

export const SelectField: React.FC<Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'id'> & { label: React.ReactNode; hint?: React.ReactNode; wrapClassName?: string; options: { value: string; label: string }[]; tag?: React.ReactNode }> = ({
  label, hint, wrapClassName, className, options, tag, ...rest
}) => {
  const id = useId();
  return (
    <Wrap id={id} label={label} hint={hint} className={wrapClassName} tag={tag}>
      <select id={id} className={cx(control, 'pr-8', className)} {...rest}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Wrap>
  );
};

/** A publish time: the hour and half-hour slots only (see lib/publishSlots). "HH:MM" in and out. */
export const TimeSlotField: React.FC<{ label: React.ReactNode; value: string; onChange: (hhmm: string) => void; hint?: React.ReactNode; wrapClassName?: string; disabled?: boolean }> = ({
  value, onChange, ...rest
}) => (
  <SelectField {...rest} value={toSlot(value)} onChange={e => onChange(e.target.value)} options={TIME_SLOTS.map(t => ({ value: t, label: t }))} />
);
